/**
 * @module engine/ops/editOps
 * @summary Delete / copy / paste / clear operations on the selection.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3715-3717, 3740.
 */
import { S, emit, markDirty } from '../state';
import { pushUndo } from '../history';
import { syncShapeCounter } from '../factory';
import type { Connection, Shape } from '../../types';

/** Delete selected shapes and any connections touching them. */
export function doDelete(): void {
  if (!S.selected.length) return;
  pushUndo();
  S.shapes = S.shapes.filter((s) => !S.selected.includes(s.id));
  S.connections = S.connections.filter(
    (c) =>
      !S.selected.includes(c.id) &&
      !S.selected.includes(c.fromId) &&
      !S.selected.includes(c.toId),
  );
  S.selected = [];
  markDirty();
  emit('selection');
  emit('doc');
}

/** Copy the selected shapes (and connections between them) to the clipboard. */
export function doCopy(): void {
  const ids = new Set(S.selected);
  S.clipboard = JSON.parse(
    JSON.stringify(S.shapes.filter((s) => ids.has(s.id))),
  ) as Shape[];
  S.clipboardConns = JSON.parse(
    JSON.stringify(S.connections.filter((c) => ids.has(c.fromId) && ids.has(c.toId))),
  ) as Connection[];
}

/** Paste clipboard shapes + their connections, offset by (20,20), and select them. */
export function doPaste(): void {
  if (!S.clipboard.length) return;
  pushUndo();
  syncShapeCounter();
  const existing = new Set([...S.shapes.map((x) => x.id), ...S.connections.map((x) => x.id)]);
  const nextId = (prefix: 's' | 'c'): string => {
    let nid: string;
    do {
      nid = prefix + ++S.shapeCounter;
    } while (existing.has(nid));
    existing.add(nid);
    return nid;
  };
  const idMap = new Map<string, string>();
  const ns = S.clipboard.map((s) => {
    const nid = nextId('s');
    idMap.set(s.id, nid);
    return { ...s, id: nid, x: s.x + 20, y: s.y + 20 };
  });
  const nc = S.clipboardConns.map((c) => ({
    ...c,
    id: nextId('c'),
    fromId: idMap.get(c.fromId)!,
    toId: idMap.get(c.toId)!,
    // Dragged waypoints are absolute world coords — shift with the shapes.
    midX: c.midX === undefined ? undefined : c.midX + 20,
    midY: c.midY === undefined ? undefined : c.midY + 20,
  }));
  S.shapes.push(...ns);
  S.connections.push(...nc);
  S.selected = ns.map((s) => s.id);
  markDirty();
  emit('selection');
  emit('doc');
}

/** Duplicate = copy + paste in one shortcut (Ctrl+D). */
export function doDuplicate(): void {
  doCopy();
  doPaste();
}

/** Clear the whole canvas (with confirmation). */
export function clearAll(): void {
  if (!confirm('Clear the entire canvas? (You can undo this with Ctrl+Z.)')) return;
  pushUndo();
  S.shapes = [];
  S.connections = [];
  S.selected = [];
  markDirty();
  emit('selection');
  emit('doc');
}

/** Select every shape. */
export function selectAll(): void {
  S.selected = S.shapes.map((s) => s.id);
  markDirty();
  emit('selection');
}
